/**
 * Which account this tab is acting as.
 *
 * Kept in sessionStorage, so switching accounts in one tab leaves the others alone, a reload keeps
 * it, and a duplicated tab keeps it in browsers that copy sessionStorage on duplicate. A tab that
 * has not chosen an account has none: the host shows an account chooser rather than guessing,
 * because a guess is how a tab ends up acting as an account the user did not pick.
 *
 * Two tabs can hold two accounts and both keep working. Before the server-side bundle the auth
 * cookie named one account per origin, so a second sign-in took the first's credential.
 */

import { readTabValue, removeTabValue, writeTabValue } from "./SessionStorage";
import { getAccountKey, getSessionRecord, touchSession } from "./SessionRegistry";

const ACTIVE_SESSION_KEY = "activeSession";

const safely = (operation, fallback = null) => {
  try {
    return operation();
  } catch (error) {
    // Private-mode browsers and blocked site data make storage throw on access. A session pointer
    // that cannot be read is "no session", never an exception thrown through a caller's render.
    return fallback;
  }
};

/**
 * The session this tab is acting as, or "" when the tab has not chosen one — a new tab, or a
 * duplicate in a browser that does not copy sessionStorage. Callers offer the signed-in accounts
 * (listSignedInSessions) instead of picking one themselves.
 */
export const getActiveSessionId = () => {
  const pinned = safely(() => readTabValue(ACTIVE_SESSION_KEY));
  if (pinned && getSessionRecord(pinned)) {
    return pinned;
  }
  if (pinned) {
    // Pinned to a session that no longer exists — logged out in another tab, or evicted.
    safely(() => removeTabValue(ACTIVE_SESSION_KEY));
  }
  return "";
};

/** Points this tab at a session. Other tabs are unaffected. */
export const setActiveSessionId = (sessionId) => {
  if (!sessionId) {
    return;
  }
  safely(() => writeTabValue(ACTIVE_SESSION_KEY, sessionId));
  touchSession(sessionId);
};

export const clearActiveSessionId = () => {
  safely(() => removeTabValue(ACTIVE_SESSION_KEY));
};

/**
 * Whether a session may issue authenticated requests: whether it has the account key that names it
 * to the server. Several accounts can hold one at once, which is what makes per-tab accounts safe.
 */
export const hasCredential = (sessionId) => Boolean(sessionId) && Boolean(getAccountKey(sessionId));
